import React from 'react';
import { RouteObject } from 'react-router-dom';

import { LearningPathPageFFWrapper } from 'src/pages/LearningPathPage/LearningPathPageFFWrapper';

import {
  AlternativeCollectionType,
  AlternativeContentHubType,
  CollectionType,
  ContentHubType,
  LandingType,
} from './constants';
import { App } from './pages/App';
import { FeedbackPage } from './pages/App/FeedbackPage';
import { ArticlePage, loader as articleLoader } from './pages/ArticlePage';
import { Calendar, loader as calendarLoader } from './pages/Calendar';
import { CatalogPage, loader as catalogLoader } from './pages/CatalogPage';
import {
  MyLearningPageWrapper,
  loader as myLearningLoader,
  loaderV2 as myLearningLoaderV2,
} from './pages/MyLearningPage';
import { CertificationDetailPage, loader as certDetailLoader } from './pages/CertificationDetailPage';
import {
  ClassPage,
  classPageLoader,
  Layout as ClassPageLayout,
  partnersClassLoader,
  PartnersPage,
  privateClassLoader,
  PrivateClassPage,
} from './pages/ClassPages';
import {
  CertificatePreparationCollectionPage,
  loader as certPrepCollectionLoader,
} from './pages/collections/CertificationPreparation';
import { loader as topicLoader, RelatedTopicCollectionPage } from './pages/collections/RelatedTopicCollectionPage';
import { loader as roleLoader, RoleBasedCollectionPage } from './pages/collections/RoleBasedCollectionPage';
import { CommunityLogin } from './pages/CommunityLogin/CommunityLogin';
import { CoursePageFFWrapper, loader as courseLoader } from './pages/CoursePage';
import { ErrorPage } from './pages/ErrorPage';
import { TenantAwareHomePage } from './pages/HomePage';
import { EnablementHomePage } from './pages/HomePage/EnablementHomePage';
import { CertificatePreparationPage, loader as certPrepLoader } from './pages/hubs/CertificatePreparationPage';
import { CertificationPage, loader as certificationLoader } from './pages/hubs/CertificationPage';
import { DynamicHubPage, loader as dynamicHubLoader } from './pages/hubs/DynamicHubPage';
import { EnablementHub, loader as enablementHubLoader } from './pages/hubs/EnablementHub';
import { JiraHubPage, loader as jiraHubLoader } from './pages/hubs/JiraHubPage';
import {
  LearningPathPage as LearningPathsHubPage,
  loader as learningPathsHubLoader,
} from './pages/hubs/LearningPathPage';
import { loader as resourcesAndSupportLoader, ResourcesAndSupportPage } from './pages/hubs/ResourcesAndSupportPage';
import { loader as roleBasedHubLoader, RoleBasedPage } from './pages/hubs/RoleBasedPage';
import { loader as rovoHubLoader, RovoHubPage } from './pages/hubs/RovoHubPage';
import { LiveTrainingPage } from './pages/landings/LiveTrainingPage';
import { loader as learningPathLoader } from './pages/LearningPathPage';
import { LessonPage, loader as lessonLoader } from './pages/LessonPage';
import {
  getCatalogUrl,
  getCertificationUrl,
  getCollectionUrl,
  getCourseUrl,
  getHubUrl,
  getLandingUrl,
  getLessonUrl,
  getMyLearningUrl,
  getPathUrl,
  LANDING_URL_PATH,
  withPrefix,
} from './utils/getUrl';

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: <TenantAwareHomePage />,
      },
      {
        path: '/enable',
        element: <EnablementHomePage />,
      },
      {
        path: '/enable/hub/:hubName',
        element: <EnablementHub />,
        loader: enablementHubLoader,
      },
      {
        path: '/community-login',
        element: <CommunityLogin />,
      },
      {
        path: getCatalogUrl(),
        element: <CatalogPage />,
        loader: catalogLoader,
      },
      {
        path: '/team-training/:courseId',
        element: <ClassPageLayout />,
        children: [
          {
            index: true,
            element: <PrivateClassPage />,
            loader: privateClassLoader,
          },
          {
            path: 'partners',
            element: <PartnersPage />,
            loader: partnersClassLoader,
          },
        ],
      },
      {
        path: '/class/:courseId',
        element: <ClassPageLayout />,
        children: [
          {
            index: true,
            element: <ClassPage />,
            loader: classPageLoader,
          },
        ],
      },
      {
        path: '/calendar',
        element: <Calendar />,
        loader: calendarLoader,
      },
      {
        path: getCourseUrl(':courseId'),
        element: <CoursePageFFWrapper />,
        loader: courseLoader,
      },
      {
        path: getCourseUrl(':courseId', ':pathId'),
        element: <CoursePageFFWrapper />,
        loader: courseLoader,
      },
      {
        path: getCourseUrl(':courseId', '', true),
        element: <FeedbackPage />,
      },
      {
        path: getCourseUrl(':courseId', ':pathId', true),
        element: <FeedbackPage />,
      },
      {
        path: getPathUrl(':pathId'),
        element: <LearningPathPageFFWrapper />,
        loader: learningPathLoader,
      },
      // My Learning V2 routes
      // hard-coded to avoid conflicts with legacy routes
      {
        path: '/my-learning/on-demand',
        element: <MyLearningPageWrapper />,
        loader: myLearningLoaderV2,
        shouldRevalidate: ({ currentUrl, nextUrl }) => {
          // Only revalidate if the pathname changes, not search params (filters/sort)
          return currentUrl.pathname !== nextUrl.pathname;
        },
      },
      {
        path: '/my-learning/live-training',
        element: <MyLearningPageWrapper />,
        loader: myLearningLoaderV2,
        shouldRevalidate: ({ currentUrl, nextUrl }) => {
          // Only revalidate if the pathname changes, not search params (filters/sort)
          return currentUrl.pathname !== nextUrl.pathname;
        },
      },
      {
        path: '/my-learning/certifications',
        element: <MyLearningPageWrapper />,
        loader: myLearningLoaderV2,
        shouldRevalidate: ({ currentUrl, nextUrl }) => {
          // Only revalidate if the pathname changes, not search params (filters/sort)
          return currentUrl.pathname !== nextUrl.pathname;
        },
      },
      // My Learning legacy routes (/in-progress, /saved, /complete)
      {
        path: getMyLearningUrl(':status'),
        element: <MyLearningPageWrapper />,
        loader: myLearningLoader,
      },
      {
        path: withPrefix(getCourseUrl(':courseId', ':pathId'), getHubUrl(ContentHubType.LEARNING_PATHS)),
        element: <CoursePageFFWrapper />,
        loader: courseLoader,
      },
      ...[
        getLessonUrl({ lessonSlug: ':lessonId' }),
        getLessonUrl({ courseSlug: ':courseId', lessonSlug: ':lessonId' }),
        getLessonUrl({ pathSlug: ':pathId', courseSlug: ':courseId', lessonSlug: ':lessonId' }),
        withPrefix(
          getLessonUrl({
            pathSlug: ':pathId',
            courseSlug: ':courseId',
            lessonSlug: ':lessonId',
          }),
          getHubUrl(ContentHubType.LEARNING_PATHS),
        ),
      ].map((path) => ({
        path,
        element: <LessonPage />,
        loader: lessonLoader,
      })),
      {
        path: withPrefix(getPathUrl(':pathId'), getHubUrl(ContentHubType.LEARNING_PATHS)),
        element: <LearningPathPageFFWrapper />,
        loader: learningPathLoader,
      },
      {
        path: getHubUrl(ContentHubType.LEARNING_PATHS),
        element: <LearningPathsHubPage />,
        loader: learningPathsHubLoader,
      },
      {
        path: getHubUrl(ContentHubType.RESOURCES_AND_SUPPORT),
        element: <ResourcesAndSupportPage />,
        loader: resourcesAndSupportLoader,
      },
      {
        path: getHubUrl(ContentHubType.RESOURCES_AND_SUPPORT, undefined, ':slug', true),
        element: <ArticlePage />,
        loader: articleLoader,
      },
      {
        path: getHubUrl(ContentHubType.ROVO),
        element: <RovoHubPage />,
        loader: rovoHubLoader,
      },
      {
        path: getHubUrl(ContentHubType.JIRA),
        element: <JiraHubPage />,
        loader: jiraHubLoader,
      },
      {
        path: '/hub/:slug',
        element: <DynamicHubPage />,
        loader: dynamicHubLoader,
      },
      {
        path: getCollectionUrl(CollectionType.TOPIC, ':topicId'),
        element: <RelatedTopicCollectionPage />,
        loader: topicLoader,
      },
      ...[
        getLandingUrl(LandingType.LIVE_TRAINING),
        withPrefix(getLandingUrl(LandingType.LIVE_TRAINING), LANDING_URL_PATH),
      ].map((path) => ({
        path,
        element: <LiveTrainingPage />,
      })),
      ...[getHubUrl(ContentHubType.CERTIFICATIONS), getCertificationUrl()].map((path) => ({
        path,
        element: <CertificationPage />,
        loader: certificationLoader,
      })),
      ...[
        withPrefix(getLandingUrl(LandingType.CERTIFICATION_DETAIL, ':certificationId'), LANDING_URL_PATH),
        getCertificationUrl(':certificationId'),
      ].map((path) => ({
        path,
        element: <CertificationDetailPage />,
        loader: certDetailLoader,
      })),
      ...[getHubUrl(AlternativeContentHubType.CERT_PREP), getHubUrl(ContentHubType.CERT_PREP)].map((path) => ({
        path,
        element: <CertificatePreparationPage />,
        loader: certPrepLoader,
      })),
      ...[getHubUrl(AlternativeContentHubType.ROLE_BASED), getHubUrl(ContentHubType.ROLE_BASED)].map((path) => ({
        path,
        element: <RoleBasedPage />,
        loader: roleBasedHubLoader,
      })),
      ...[
        getCollectionUrl(AlternativeCollectionType.CERT_PREP, ':certPrepId'),
        getCollectionUrl(CollectionType.CERT_PREP, ':certPrepId'),
      ].map((path) => ({
        path,
        element: <CertificatePreparationCollectionPage />,
        loader: certPrepCollectionLoader,
      })),
      ...[
        getCollectionUrl(AlternativeCollectionType.ROLE_BASED, ':roleId'),
        getCollectionUrl(CollectionType.ROLE_BASED, ':roleId'),
      ].map((path) => ({
        path,
        element: <RoleBasedCollectionPage />,
        loader: roleLoader,
      })),
    ],
  },
];
